import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Log } from '@/utils/logger';

interface UserProfile {
  first_name?: string;
  last_name?: string;
  email?: string;
  user_tier?: 'Free' | 'Premium';
}

interface UserData {
  profile: UserProfile | null;
  role: string;
  isAdmin: boolean;
  isLoading: boolean;
  refreshUserData: () => Promise<void>;
}

const UserDataContext = createContext<UserData | undefined>(undefined);

// Cache with 5-minute expiration
const CACHE_DURATION = 5 * 60 * 1000;
const cache = new Map<string, { data: any; timestamp: number }>();

export const UserDataProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [role, setRole] = useState<string>('user');
  const [isLoading, setIsLoading] = useState(true);
  const abortControllerRef = useRef<AbortController>();

  const fetchUserData = async (userId: string, forceRefresh = false) => {
    // Check cache first
    const cacheKey = `user_data_${userId}`;
    const cached = cache.get(cacheKey);
    const now = Date.now();

    if (!forceRefresh && cached && (now - cached.timestamp < CACHE_DURATION)) {
      setProfile(cached.data.profile);
      setRole(cached.data.role);
      setIsLoading(false);
      return;
    }

    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);

      // Fetch profile and role in parallel
      const [profileResult, roleResult] = await Promise.all([
        supabase.rpc('get_profile_summary', { _user_id: userId }),
        supabase.from('user_roles').select('role').eq('user_id', userId).single()
      ]);

      if (abortControllerRef.current?.signal.aborted) return;

      const userData = {
        profile: profileResult.data?.[0] || null,
        role: roleResult.data?.role || 'user'
      };

      // Update cache
      cache.set(cacheKey, {
        data: userData,
        timestamp: now
      });

      setProfile(userData.profile);
      setRole(userData.role);
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        Log.error('Error fetching user data', { error: error.message });
      }
    } finally {
      if (!abortControllerRef.current?.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const refreshUserData = async () => {
    if (user?.id) {
      await fetchUserData(user.id, true);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchUserData(user.id);
    } else {
      // Clear data when logged out
      setProfile(null);
      setRole('user');
      setIsLoading(false);
      // Clear cache for this user
      if (user) {
        cache.delete(`user_data_${user.id}`);
      }
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [user?.id]);

  // Clean up old cache entries periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      const now = Date.now();
      for (const [key, value] of cache.entries()) {
        if (now - value.timestamp > CACHE_DURATION) {
          cache.delete(key);
        }
      }
    }, CACHE_DURATION);

    return () => clearInterval(cleanupInterval);
  }, []);

  const value: UserData = {
    profile,
    role,
    isAdmin: role === 'admin',
    isLoading,
    refreshUserData
  };

  return <UserDataContext.Provider value={value}>{children}</UserDataContext.Provider>;
};

export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
};
